import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthAPI, API_BASE_URL } from '../../api/client';
import { AuthLayout, ErrorText, InfoText, useAuthConfig, input, label, button, linkRow, linkStyle } from './authShared';

/** Sign-in screen. In mock mode it bounces to the backend dev role-chooser so the
 *  local workflow is untouched; in cognito mode it renders the custom form. */
export default function Login() {
  const cfg = useAuthConfig();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const notice = params.get('verified') ? 'Your email is verified — please sign in.'
    : params.get('reset') ? 'Your password has been reset — please sign in.'
    : '';

  // Mock mode: hand off to the dev chooser (server-rendered at /api/auth/login).
  useEffect(() => {
    if (cfg?.mode === 'mock') window.location.assign(`${API_BASE_URL}/auth/login`);
  }, [cfg]);

  if (!cfg || cfg.mode === 'mock') {
    return <AuthLayout title="FIL Portal" subtitle="Loading…"><span /></AuthLayout>;
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password) { setError('Enter your email and password.'); return; }
    setBusy(true); setError('');
    const r = await AuthAPI.login(email.trim(), password);
    if (r.ok && r.status === 'ok') { window.location.assign('/'); return; }
    setBusy(false);
    if (r.status === 'unconfirmed') { nav(`/verify?email=${encodeURIComponent(email.trim())}`); return; }
    if (r.status === 'new_password_required') { nav('/new-password'); return; }
    if (r.status === 'mfa_required') { nav('/mfa'); return; }
    if (r.status === 'reset_required') {
      nav(`/forgot-password?email=${encodeURIComponent(email.trim())}&reason=reset_required`); return;
    }
    setError(r.message || 'Incorrect email or password.');
  };

  return (
    <AuthLayout title="Sign in" subtitle="Fintech Innovation Lab Portal">
      <form onSubmit={submit}>
        {notice && <InfoText>{notice}</InfoText>}
        <label style={label}>Email
          <input style={input} type="email" autoComplete="username" value={email}
                 onChange={e => setEmail(e.target.value)} autoFocus />
        </label>
        <label style={label}>Password
          <input style={input} type="password" autoComplete="current-password" value={password}
                 onChange={e => setPassword(e.target.value)} />
        </label>
        <div style={{ margin: '-.4rem 0 .9rem', textAlign: 'right' }}>
          <Link to="/forgot-password" style={{ ...linkStyle, fontSize: '.8rem' }}>Forgot password?</Link>
        </div>
        <ErrorText>{error}</ErrorText>
        <button type="submit" style={button} disabled={busy}>{busy ? 'Signing in…' : 'Sign in'}</button>
      </form>
      <p style={linkRow}>Need an account? <Link to="/signup" style={linkStyle}>Create one</Link></p>
    </AuthLayout>
  );
}
